import clsx from "clsx";
import { useTranslation } from "@client-app/i18n";
import Modal from "../Modal";
import IntegrationConnectGuide, {
  getIntegrationConnectGuide,
  getIntegrationConnectGuideModalWidth,
} from "./IntegrationConnectGuide";

interface UrlInputConfig {
  prefix: string;
  suffix: string;
  placeholder: string;
  fieldLabel?: string;
  helperText?: string;
}

interface IntegrationExtraField {
  key: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

interface IntegrationUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolName: string;
  urlConfig?: UrlInputConfig;
  storeUrl?: string;
  onStoreUrlChange?: (value: string) => void;
  /** @deprecated Business-group assignment occurs after connection succeeds. */
  connectionAlias?: string;
  onConnectionAliasChange?: (value: string) => void;

  onConfirm: () => void;
  isLoading?: boolean;
  errorText?: string | null;
  extraFields?: IntegrationExtraField[];
  brandOptions?: unknown[];
  selectedBrandIds?: string[];
  onSelectedBrandIdsChange?: (brandIds: string[]) => void;
}

export default function IntegrationUrlModal({
  isOpen,
  onClose,
  toolName,
  urlConfig,
  storeUrl = "",
  onStoreUrlChange,
  onConfirm,
  isLoading = false,
  errorText = null,
  extraFields = [],
}: IntegrationUrlModalProps) {
  const { t } = useTranslation();
  // 如果未打开，不渲染任何内容
  if (!isOpen) return null;

  const connectGuide = getIntegrationConnectGuide(toolName);
  const modalWidth = getIntegrationConnectGuideModalWidth(toolName, "max-w-md");
  const modalHeaderClassName = connectGuide
    ? "px-6 pb-4 pt-5"
    : "px-6 pb-2 pt-5";
  const modalBodyClassName = connectGuide ? "px-6 pb-6 pt-0" : "px-6 pb-6 pt-1";
  const contentSpacingClassName = connectGuide ? "space-y-4" : "space-y-3";
  const modalTitleClassName = "text-[#1A162F]";

  const canConfirm =
    (!urlConfig || Boolean(storeUrl.trim())) &&
    extraFields.every((field) => Boolean(field.value.trim()));

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canConfirm && !isLoading) {
      onConfirm();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!urlConfig) return;

    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    // 根据配置的前缀和后缀截掉粘贴的文本
    let cleanedText = pastedText.trim();

    // 移除前缀
    if (urlConfig.prefix && cleanedText.startsWith(urlConfig.prefix)) {
      cleanedText = cleanedText.slice(urlConfig.prefix.length);
    }

    // 移除后缀
    if (urlConfig.suffix && cleanedText.endsWith(urlConfig.suffix)) {
      cleanedText = cleanedText.slice(0, -urlConfig.suffix.length);
    }

    // 移除可能的开头斜杠
    cleanedText = cleanedText.replace(/^\/+/, "").trim();

    if (onStoreUrlChange) {
      onStoreUrlChange(cleanedText);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("website.integrationsSettings.connectToolTitle", {
        tool: toolName,
      })}
      width={modalWidth}
      headerClassName={modalHeaderClassName}
      titleClassName={modalTitleClassName}
      bodyClassName={modalBodyClassName}
    >
      <div className={contentSpacingClassName}>
        <IntegrationConnectGuide toolName={toolName} guide={connectGuide} />

        {urlConfig && onStoreUrlChange ? (
          <div>
            <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
              {urlConfig.fieldLabel ||
                t("website.integrationsSettings.toolStoreUrl", {
                  tool: toolName,
                })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center rounded-xl border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[#1A162F]/20 focus-within:border-[#1A162F] transition-all">
              {urlConfig.prefix ? (
                <span className="select-none whitespace-nowrap border-r border-gray-100 bg-gray-50/50 py-2.5 pl-3 pr-1 text-sm text-[#666F8D]">
                  {urlConfig.prefix}
                </span>
              ) : null}
              <input
                type="text"
                value={storeUrl}
                onChange={(e) => onStoreUrlChange(e.target.value)}
                onPaste={handlePaste}
                placeholder={urlConfig.placeholder}
                className="min-w-0 flex-1 bg-transparent px-2 py-2.5 text-sm text-[#1A162F] focus:outline-none placeholder:text-[#666F8D]"
                onKeyDown={handleKeyDown}
              />
              {urlConfig.suffix ? (
                <span className="select-none whitespace-nowrap border-l border-gray-100 bg-gray-50/50 py-2.5 pl-1 pr-3 text-sm text-[#666F8D]">
                  {urlConfig.suffix}
                </span>
              ) : null}
            </div>
            {urlConfig.helperText ? (
              <p className="mt-2 text-xs leading-5 text-[#666F8D]">
                {urlConfig.helperText}
              </p>
            ) : null}
            {errorText ? (
              <p className="mt-2 text-xs leading-5 text-red-600">{errorText}</p>
            ) : null}
          </div>
        ) : null}
        {extraFields.map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-semibold text-text-main text-gray-900 mb-2">
              {field.label}
            </label>
            <input
              type="text"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              placeholder={field.placeholder}
              className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm text-text-main text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
              onKeyDown={handleKeyDown}
            />
          </div>
        ))}
        <div
          className={clsx(
            "flex justify-end gap-3",
            connectGuide ? "pt-1" : "pt-2",
          )}
        >
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-lg px-4 py-2 text-sm font-medium text-[#666F8D] transition-colors hover:bg-gray-100 hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm || isLoading}
            className={clsx(
              "px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2",
              canConfirm && !isLoading
                ? "bg-[#1A162F] text-white hover:bg-primary/90"
                : "cursor-not-allowed bg-gray-300 text-[#666F8D]",
            )}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>{t("website.integrationsSettings.connecting")}</span>
              </>
            ) : (
              t("website.integrationsSettings.connectToolAction", {
                tool: toolName,
              })
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
